import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

// Import du fichier local en fallback si l'API n'est pas disponible
import localContractDocumentation from "./contract_documentation.json";
import { api_url } from "../../config/config";

// Constante pour le type de mapping utilisé
const MAPPING_TYPE = "VEHICULE_FLOTTE";

const useETL = () => {
  const [correspondances, setCorrespondances] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMapped, setFilterMapped] = useState(false);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [modifiedDocumentation, setModifiedDocumentation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mappingUuid, setMappingUuid] = useState(null);
  const [contractDocumentation, setContractDocumentation] = useState(
    localContractDocumentation
  );

  // Référence pour stocker le timeout de debounce
  const saveTimeoutRef = useRef(null);

  // Fonction pour charger les mappings depuis l'API
  const loadMappingsFromApi = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Récupérer les mappings du type VEHICULE_FLOTTE
      const response = await axios.get(
        `${api_url}mappings/type/${MAPPING_TYPE}`
      );

      if (response.data) {
        // Convertir la chaîne JSON en objet si nécessaire
        const jsonData =
          typeof response.data.json === "string"
            ? JSON.parse(response.data.json)
            : response.data.json;
        console.log("jsonData", jsonData);
        setContractDocumentation(jsonData);
        return jsonData;
      }
    } catch (err) {
      setError("Erreur lors du chargement des mappings: " + err.message);
      console.error("Erreur de chargement des mappings:", err);
      // Fallback sur la version locale en cas d'erreur
      setContractDocumentation(localContractDocumentation);
      return localContractDocumentation;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour sauvegarder le mapping en API
  const saveMappingToApi = useCallback(
    async (updatedDoc) => {
      setLoading(true);
      setError(null);
      try {
        console.log("updatedDoc", updatedDoc);
        const payload = {
          type: MAPPING_TYPE,
          json: JSON.stringify(updatedDoc),
        };

        // Ajouter l'UUID si on a modifié un mapping existant
        if (mappingUuid) {
          payload.uuid = mappingUuid;
        }

        const response = await axios.post(`${api_url}mappings`, payload);

        if (response.data && response.data.uuid) {
          setMappingUuid(response.data.uuid);
          setModifiedDocumentation(false);
          console.log("Mapping sauvegardé automatiquement");
          return true;
        }
      } catch (err) {
        setError("Erreur lors de la sauvegarde du mapping: " + err.message);
        console.error("Erreur de sauvegarde:", err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [mappingUuid]
  );

  // Fonction pour traiter la documentation et créer les correspondances
  const processDocumentation = useCallback((docData) => {
    // Construire un dictionnaire des attributs par colonne_excel pour le type de document actif
    const docByExcelColumn = {};

    // Fonction récursive pour traiter les attributs à tous les niveaux
    const processAttributes = (attributes, parentPath = "") => {
      Object.entries(attributes).forEach(([attrName, attrDetails]) => {
        const currentPath = parentPath ? `${parentPath}.${attrName}` : attrName;

        if (attrDetails.colonne_excel) {
          docByExcelColumn[attrDetails.colonne_excel] = {
            ...attrDetails,
            path: currentPath,
          };
        }

        // Traitement récursif des sous-attributs si présents
        if (attrDetails.attributes) {
          processAttributes(attrDetails.attributes, currentPath);
        }
      });
    };

    // Démarrer le traitement avec les attributs racine
    processAttributes(docData.attributes);

    // Créer la liste des correspondances en utilisant maintenant contract_documentation.columns
    const mappings = Object.entries(docData.columns || {}).map(
      ([colName, colDetails]) => {
        // Recherche de l'attribut correspondant
        const matchingAttribute = docByExcelColumn[colName] || null;

        return {
          csvColumn: colName,
          csvType: colDetails.type,
          csvDescription: colDetails.description,
          importer:
            colDetails.importer !== undefined ? colDetails.importer : false,
          docPath: matchingAttribute ? matchingAttribute.path : null,
          docTitle: matchingAttribute ? matchingAttribute.title : null,
          docType: matchingAttribute ? matchingAttribute.type : null,
        };
      }
    );

    setCorrespondances(mappings);
  }, []);

  // Extraction des attributs disponibles pour l'édition
  useEffect(() => {
    const extractAttributes = (attributes, parentPath = "") => {
      let result = [];

      Object.entries(attributes).forEach(([attrName, attrDetails]) => {
        const currentPath = parentPath ? `${parentPath}.${attrName}` : attrName;

        result.push({
          path: currentPath,
          title: attrDetails.title || attrName,
          type: attrDetails.type || "unknown",
        });

        if (attrDetails.attributes) {
          result = [
            ...result,
            ...extractAttributes(attrDetails.attributes, currentPath),
          ];
        }
      });

      return result;
    };

    const allAttributes = extractAttributes(contractDocumentation.attributes);
    setAvailableAttributes(
      allAttributes.sort((a, b) => a.path.localeCompare(b.path))
    );
  }, [contractDocumentation]);

  // Chargement initial des données depuis l'API
  useEffect(() => {
    const initData = async () => {
      const data = await loadMappingsFromApi();
      if (data) {
        processDocumentation(data);
      }
    };

    initData();
  }, [loadMappingsFromApi, processDocumentation]);

  // Fonction pour préparer la documentation mise à jour
  const prepareUpdatedDocumentation = useCallback((correspondancesToUse = null) => {
    // Utiliser les correspondances fournies ou l'état actuel
    const currentCorrespondances = correspondancesToUse || correspondances;
    
    // Création d'une copie du document actuel
    const updatedContractDoc = { ...contractDocumentation };

    // Réinitialiser d'abord toutes les colonnes_excel
    const resetAttributeColumns = (attributes) => {
      Object.keys(attributes).forEach((key) => {
        if (attributes[key].colonne_excel) {
          attributes[key].colonne_excel = "";
        }
        if (attributes[key].attributes) {
          resetAttributeColumns(attributes[key].attributes);
        }
      });
    };

    resetAttributeColumns(updatedContractDoc.attributes);

    // Mettre à jour les descriptions et l'attribut importer dans columns
    if (updatedContractDoc.columns) {
      currentCorrespondances.forEach((item) => {
        if (updatedContractDoc.columns[item.csvColumn]) {
          // Mettre à jour la description et l'attribut importer
          updatedContractDoc.columns[item.csvColumn].description =
            item.csvDescription;
          updatedContractDoc.columns[item.csvColumn].importer =
            item.importer || false;
        }
      });
    }

    // Appliquer les nouvelles correspondances
    currentCorrespondances.forEach((item) => {
      if (item.docPath) {
        const pathParts = item.docPath.split(".");
        let currentObj = updatedContractDoc.attributes;

        // Navigue dans l'objet selon le chemin
        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i];
          if (i === pathParts.length - 1) {
            // Dernier élément du chemin, mettre à jour colonne_excel
            if (currentObj[part]) {
              currentObj[part].colonne_excel = item.csvColumn;
            }
          } else {
            // Navigation dans la structure
            if (currentObj[part] && currentObj[part].attributes) {
              currentObj = currentObj[part].attributes;
            } else {
              break; // Chemin invalide
            }
          }
        }
      }
    });

    return updatedContractDoc;
  }, [contractDocumentation, correspondances]);

  // Fonction pour sauvegarder avec délai
  const debouncedSave = useCallback((updatedCorrespondancesData = null) => {
    // Annuler le timeout précédent s'il existe
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Créer un nouveau timeout
    saveTimeoutRef.current = setTimeout(async () => {
      // Si des correspondances spécifiques sont fournies, on les utilise pour générer le doc
      // sinon on utilise l'état actuel
      const updatedDoc = updatedCorrespondancesData 
        ? prepareUpdatedDocumentation(updatedCorrespondancesData) 
        : prepareUpdatedDocumentation();
      await saveMappingToApi(updatedDoc);
    }, 500); // Délai de 500ms
  }, [prepareUpdatedDocumentation, saveMappingToApi]);

  // Fonctions de modification des correspondances
  const handleSelectChange = useCallback(
    (e, index) => {
      const selectedPath = e.target.value;
      const selectedAttribute = availableAttributes.find(
        (attr) => attr.path === selectedPath
      );

      // Mettre à jour la correspondance
      const updatedCorrespondances = [...correspondances];
      updatedCorrespondances[index] = {
        ...updatedCorrespondances[index],
        docPath: selectedPath,
        docTitle: selectedAttribute ? selectedAttribute.title : null,
        docType: selectedAttribute ? selectedAttribute.type : null,
      };

      setCorrespondances(updatedCorrespondances);
      setModifiedDocumentation(true); // Indique qu'il y a eu des modifications

      // Déclencher la sauvegarde avec délai en passant explicitement les données mises à jour
      debouncedSave(updatedCorrespondances);
    },
    [availableAttributes, correspondances, debouncedSave]
  );

  const handleDescriptionChange = useCallback(
    (e, index) => {
      const updatedCorrespondances = [...correspondances];
      updatedCorrespondances[index] = {
        ...updatedCorrespondances[index],
        csvDescription: e.target.value,
      };
      setCorrespondances(updatedCorrespondances);
      setModifiedDocumentation(true); // Indique qu'il y a eu des modifications

      // Déclencher la sauvegarde avec délai en passant explicitement les données mises à jour
      debouncedSave(updatedCorrespondances);
    },
    [correspondances, debouncedSave]
  );

  const handleImporterChange = useCallback(
    (e, index) => {
      console.log("e.target.checked", e.target.checked);
      const updatedCorrespondances = [...correspondances];
      updatedCorrespondances[index] = {
        ...updatedCorrespondances[index],
        importer: e.target.checked,
      };
      console.log("updatedCorrespondances", updatedCorrespondances);
      setCorrespondances(updatedCorrespondances);
      setModifiedDocumentation(true); // Indique qu'il y a eu des modifications

      // Déclencher la sauvegarde avec délai en passant explicitement les données mises à jour
      debouncedSave(updatedCorrespondances);
    },
    [correspondances, debouncedSave]
  );

  // Filtrer les correspondances en fonction de la recherche
  const filteredCorrespondances = correspondances.filter((item) => {
    const matchesSearch =
      searchTerm === "" ||
      item.csvColumn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.csvDescription &&
        item.csvDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.docTitle &&
        item.docTitle.toLowerCase().includes(searchTerm.toLowerCase()));

    // Si le filtre "Mapped only" est activé, n'afficher que les éléments mappés
    if (filterMapped && !item.docPath) {
      return false;
    }

    return matchesSearch;
  });

  return {
    correspondances,
    filteredCorrespondances,
    availableAttributes,
    modifiedDocumentation,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filterMapped,
    setFilterMapped,
    handleSelectChange,
    handleDescriptionChange,
    handleImporterChange,
    loadMappingsFromApi,
    mappingUuid,
  };
};

export default useETL;
